import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/hooks/use-toast";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Shield,
  Mail,
  Plus,
  Search,
  Edit,
  Trash2,
  Eye,
  Send,
  Code,
  FileText,
  Monitor,
  Smartphone,
  Copy,
  Check,
  X,
  Sparkles,
  Palette,
  Variable,
  AlertCircle,
  RefreshCw,
  History,
  CheckCircle2,
  XCircle,
  Clock,
  ChevronLeft,
  ChevronRight,
  Inbox,
  Upload,
  ImageIcon,
  Loader2,
} from "lucide-react";
import { cn } from "@/lib/utils";

// Types
interface EmailTemplate {
  id: string;
  templateKey: string;
  name: string;
  description: string | null;
  category: "TRANSACTIONAL" | "MARKETING" | "NOTIFICATION" | "SYSTEM";
  subject: string;
  htmlContent: string;
  textContent: string | null;
  styling: {
    primaryColor?: string;
    secondaryColor?: string;
    logoUrl?: string;
    fontFamily?: string;
    headerBackground?: string;
    footerText?: string;
  } | null;
  variables: Array<{
    name: string;
    description: string;
    required: boolean;
    defaultValue?: string;
    example?: string;
  }> | null;
  isActive: boolean;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
}

interface TemplateFormData {
  templateKey: string;
  name: string;
  description: string;
  category: "TRANSACTIONAL" | "MARKETING" | "NOTIFICATION" | "SYSTEM";
  subject: string;
  htmlContent: string;
  textContent: string;
  isActive: boolean;
  variables: Array<{
    name: string;
    description: string;
    required: boolean;
    defaultValue?: string;
    example?: string;
  }>;
  styling: {
    primaryColor: string;
    secondaryColor: string;
    logoUrl: string;
    fontFamily: string;
    headerBackground: string;
    footerText: string;
  };
}

interface EmailLog {
  id: string;
  templateId: string | null;
  templateKey: string | null;
  recipientEmail: string;
  recipientName: string | null;
  subject: string;
  status: "queued" | "sent" | "delivered" | "failed" | "bounced";
  errorMessage: string | null;
  resendMessageId: string | null;
  customerId: string | null;
  metadata: Record<string, unknown> | null;
  sentAt: string | null;
  createdAt: string;
}

const statusConfig: Record<string, { label: string; className: string; icon: typeof Check }> = {
  queued: { label: "Queued", className: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300", icon: Clock },
  sent: { label: "Sent", className: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300", icon: Send },
  delivered: { label: "Delivered", className: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300", icon: CheckCircle2 },
  failed: { label: "Failed", className: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300", icon: XCircle },
  bounced: { label: "Bounced", className: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300", icon: AlertCircle },
};

// Default email template HTML
const defaultTemplateHtml = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>{{subject}}</title>
  <style>
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; margin: 0; padding: 0; background-color: #f4f4f5; }
    .container { max-width: 600px; margin: 0 auto; padding: 20px; }
    .card { background: white; border-radius: 12px; box-shadow: 0 2px 8px rgba(0,0,0,0.08); overflow: hidden; }
    .header { background: linear-gradient(135deg, #2BA88F 0%, #1D8A75 100%); padding: 32px; text-align: center; }
    .header h1 { color: white; margin: 0; font-size: 24px; font-weight: 600; }
    .content { padding: 32px; }
    .content h2 { color: #18181b; margin: 0 0 16px; font-size: 20px; }
    .content p { color: #52525b; line-height: 1.6; margin: 0 0 16px; }
    .button { display: inline-block; background: #2BA88F; color: white; padding: 12px 24px; border-radius: 8px; text-decoration: none; font-weight: 500; }
    .footer { padding: 24px 32px; background: #fafafa; border-top: 1px solid #e4e4e7; text-align: center; }
    .footer p { color: #71717a; font-size: 12px; margin: 0; }
  </style>
</head>
<body>
  <div class="container">
    <div class="card">
      <div class="header">
        <h1>VedaLogic</h1>
      </div>
      <div class="content">
        <h2>Hello {{recipientName}},</h2>
        <p>{{messageContent}}</p>
        <p style="text-align: center; margin: 24px 0;">
          <a href="{{actionUrl}}" class="button">{{actionText}}</a>
        </p>
      </div>
      <div class="footer">
        <p>&copy; 2026 VedaLogic. All rights reserved.</p>
      </div>
    </div>
  </div>
</body>
</html>`;

const defaultVariables = [
  { name: "subject", description: "Email subject line", required: true, example: "Welcome to VedaLogic" },
  { name: "recipientName", description: "Recipient's name", required: true, example: "John Doe" },
  { name: "messageContent", description: "Main message content", required: true, example: "Thank you for joining us!" },
  { name: "actionUrl", description: "Call-to-action button URL", required: false, example: "https://vedalogic.io/dashboard" },
  { name: "actionText", description: "Call-to-action button text", required: false, example: "Get Started" },
];

const defaultStyling = {
  primaryColor: "#2BA88F",
  secondaryColor: "#1D8A75",
  logoUrl: "",
  fontFamily: "",
  headerBackground: "",
  footerText: "\u00A9 2026 VedaLogic. All rights reserved.",
};

const fontFamilyOptions = [
  { label: "System Default", value: "" },
  { label: "Arial", value: "Arial, Helvetica, sans-serif" },
  { label: "Georgia", value: "Georgia, 'Times New Roman', serif" },
  { label: "Verdana", value: "Verdana, Geneva, sans-serif" },
  { label: "Tahoma", value: "Tahoma, Geneva, sans-serif" },
  { label: "Trebuchet MS", value: "'Trebuchet MS', Helvetica, sans-serif" },
  { label: "Times New Roman", value: "'Times New Roman', Times, serif" },
];

// Category badge colors
const categoryColors: Record<string, { bg: string; text: string }> = {
  TRANSACTIONAL: { bg: "bg-blue-500/10", text: "text-blue-600 dark:text-blue-400" },
  MARKETING: { bg: "bg-purple-500/10", text: "text-purple-600 dark:text-purple-400" },
  NOTIFICATION: { bg: "bg-amber-500/10", text: "text-amber-600 dark:text-amber-400" },
  SYSTEM: { bg: "bg-slate-500/10", text: "text-slate-600 dark:text-slate-400" },
};

// Logo Upload Field Component
function LogoUploadField({
  logoUrl,
  onLogoChange,
}: {
  logoUrl: string;
  onLogoChange: (url: string) => void;
}) {
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const handleUpload = useCallback(async (file: File) => {
    if (!file.type.startsWith("image/")) {
      toast({ title: "Invalid file", description: "Please upload an image file (PNG, JPG, SVG, etc.)", variant: "destructive" });
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast({ title: "File too large", description: "Logo must be under 2 MB", variant: "destructive" });
      return;
    }

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append("logo", file);
      const response = await fetch("/api/upload/logo", {
        method: "POST",
        body: formData,
        credentials: "include",
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || "Upload failed");
      }
      const { url } = await response.json();
      onLogoChange(url);
      toast({ title: "Logo uploaded", description: "Your logo has been uploaded successfully." });
    } catch (error) {
      toast({
        title: "Upload failed",
        description: error instanceof Error ? error.message : "Could not upload logo",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  }, [onLogoChange, toast]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleUpload(file);
  }, [handleUpload]);

  return (
    <div className="space-y-3">
      <Label className="text-xs">Logo</Label>

      {/* Preview current logo */}
      {logoUrl && (
        <div className="flex items-center gap-3 p-3 border rounded-lg bg-muted/30">
          <div className="h-12 w-28 flex items-center justify-center bg-white dark:bg-slate-800 rounded border overflow-hidden p-1">
            <img
              src={logoUrl}
              alt="Logo preview"
              className="max-h-full max-w-full object-contain"
              onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
            />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium truncate">
              {(() => {
                try {
                  const url = new URL(logoUrl);
                  const segments = url.pathname.split("/");
                  return segments[segments.length - 1] || "logo";
                } catch {
                  return logoUrl.split("/").pop() || "logo";
                }
              })()}
            </p>
            <p className="text-[10px] text-muted-foreground truncate mt-0.5">{logoUrl}</p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="text-destructive hover:text-destructive h-8 w-8 p-0"
            onClick={() => onLogoChange("")}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Upload zone */}
      {!logoUrl && (
        <div
          className={cn(
            "border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer",
            isDragging
              ? "border-primary bg-primary/5"
              : "border-muted-foreground/25 hover:border-primary/50 hover:bg-muted/30"
          )}
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          {isUploading ? (
            <div className="flex flex-col items-center gap-2">
              <Loader2 className="h-8 w-8 text-primary animate-spin" />
              <p className="text-sm text-muted-foreground">Uploading...</p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <ImageIcon className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-sm font-medium">Drop your logo here or click to upload</p>
                <p className="text-xs text-muted-foreground mt-0.5">PNG, JPG, or SVG up to 2 MB</p>
              </div>
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleUpload(file);
              e.target.value = "";
            }}
          />
        </div>
      )}

      {/* Manual URL fallback */}
      {!logoUrl && (
        <div className="flex items-center gap-2">
          <div className="flex-1 h-px bg-border" />
          <span className="text-xs text-muted-foreground">or paste a URL</span>
          <div className="flex-1 h-px bg-border" />
        </div>
      )}
      {!logoUrl && (
        <Input
          value=""
          onChange={(e) => {
            if (e.target.value) onLogoChange(e.target.value);
          }}
          onPaste={(e) => {
            const text = e.clipboardData.getData("text");
            if (text && (text.startsWith("http://") || text.startsWith("https://"))) {
              e.preventDefault();
              onLogoChange(text);
            }
          }}
          placeholder="https://example.com/logo.png"
          className="text-sm"
        />
      )}

      <p className="text-xs text-muted-foreground">
        Replaces the text header with your logo image in the email template.
      </p>
    </div>
  );
}

// Live Preview Component
function LivePreview({
  htmlContent,
  variables,
  previewVariables,
  styling,
}: {
  htmlContent: string;
  variables: Array<{ name: string; example?: string }>;
  previewVariables: Record<string, string>;
  styling?: TemplateFormData["styling"];
}) {
  const [viewMode, setViewMode] = useState<"desktop" | "mobile">("desktop");

  // Render the HTML with variables replaced and branding applied
  const renderedHtml = useMemo(() => {
    let html = htmlContent;

    // Replace template variables
    variables.forEach((v) => {
      const value = previewVariables[v.name] || v.example || `{{${v.name}}}`;
      const regex = new RegExp(`\\{\\{\\s*${v.name}\\s*\\}\\}`, "g");
      html = html.replace(regex, value);
    });

    // Apply branding overrides
    if (styling) {
      if (styling.primaryColor && styling.primaryColor !== "#2BA88F") {
        html = html.replace(/#2BA88F/gi, styling.primaryColor);
      }
      if (styling.secondaryColor && styling.secondaryColor !== "#1D8A75") {
        html = html.replace(/#1D8A75/gi, styling.secondaryColor);
      }
      if (styling.headerBackground) {
        html = html.replace(
          /background:\s*linear-gradient\(135deg,\s*[^)]+\)/i,
          `background: ${styling.headerBackground}`
        );
      }
      if (styling.logoUrl) {
        html = html.replace(
          /<h1>VedaLogic<\/h1>/i,
          `<img src="${styling.logoUrl}" alt="Logo" style="max-height: 40px; width: auto;" />`
        );
      }
      if (styling.footerText && styling.footerText !== defaultStyling.footerText) {
        html = html.replace(
          /&copy; 2026 VedaLogic\. All rights reserved\./,
          styling.footerText.replace(/©/g, "&copy;")
        );
      }
      if (styling.fontFamily) {
        html = html.replace(
          /font-family:\s*-apple-system[^;]*/,
          `font-family: ${styling.fontFamily}`
        );
      }
    }

    return html;
  }, [htmlContent, variables, previewVariables, styling]);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">Live Preview</Label>
        <div className="flex items-center gap-1 p-1 bg-muted rounded-lg">
          <Button
            variant="ghost"
            size="sm"
            className={cn("h-7 px-2", viewMode === "desktop" && "bg-background shadow-sm")}
            onClick={() => setViewMode("desktop")}
          >
            <Monitor className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className={cn("h-7 px-2", viewMode === "mobile" && "bg-background shadow-sm")}
            onClick={() => setViewMode("mobile")}
          >
            <Smartphone className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <div
        className={cn(
          "border rounded-lg bg-white dark:bg-slate-900 overflow-hidden transition-all duration-300 mx-auto",
          viewMode === "desktop" ? "w-full" : "w-[375px]"
        )}
      >
        <div className="bg-muted/50 px-3 py-2 border-b flex items-center gap-2">
          <div className="flex gap-1.5">
            <div className="w-3 h-3 rounded-full bg-red-400" />
            <div className="w-3 h-3 rounded-full bg-amber-400" />
            <div className="w-3 h-3 rounded-full bg-green-400" />
          </div>
          <div className="flex-1 text-center text-xs text-muted-foreground">
            Email Preview
          </div>
        </div>
        <iframe
          srcDoc={renderedHtml}
          className={cn(
            "w-full border-0 bg-white",
            viewMode === "desktop" ? "h-[500px]" : "h-[600px]"
          )}
          sandbox="allow-same-origin"
          title="Email Preview"
        />
      </div>
    </div>
  );
}

// Template Editor Dialog
function TemplateEditorDialog({
  open,
  onOpenChange,
  template,
  onSave,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  template: EmailTemplate | null;
  onSave: (data: TemplateFormData) => void;
}) {
  const [formData, setFormData] = useState<TemplateFormData>({
    templateKey: "",
    name: "",
    description: "",
    category: "TRANSACTIONAL",
    subject: "",
    htmlContent: defaultTemplateHtml,
    textContent: "",
    isActive: true,
    variables: [...defaultVariables],
    styling: { ...defaultStyling },
  });
  const [activeTab, setActiveTab] = useState("content");
  const [previewVariables, setPreviewVariables] = useState<Record<string, string>>({});
  const [copiedField, setCopiedField] = useState<string | null>(null);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      if (template) {
        setFormData({
          templateKey: template.templateKey,
          name: template.name,
          description: template.description || "",
          category: template.category,
          subject: template.subject,
          htmlContent: template.htmlContent,
          textContent: template.textContent || "",
          isActive: template.isActive,
          variables: template.variables || [...defaultVariables],
          styling: {
            primaryColor: template.styling?.primaryColor || defaultStyling.primaryColor,
            secondaryColor: template.styling?.secondaryColor || defaultStyling.secondaryColor,
            logoUrl: template.styling?.logoUrl || defaultStyling.logoUrl,
            fontFamily: template.styling?.fontFamily || defaultStyling.fontFamily,
            headerBackground: template.styling?.headerBackground || defaultStyling.headerBackground,
            footerText: template.styling?.footerText || defaultStyling.footerText,
          },
        });
        // Initialize preview variables from examples
        const vars: Record<string, string> = {};
        (template.variables || []).forEach((v) => {
          if (v.example) vars[v.name] = v.example;
        });
        setPreviewVariables(vars);
      } else {
        setFormData({
          templateKey: "",
          name: "",
          description: "",
          category: "TRANSACTIONAL",
          subject: "",
          htmlContent: defaultTemplateHtml,
          textContent: "",
          isActive: true,
          variables: [...defaultVariables],
          styling: { ...defaultStyling },
        });
        const vars: Record<string, string> = {};
        defaultVariables.forEach((v) => {
          if (v.example) vars[v.name] = v.example;
        });
        setPreviewVariables(vars);
      }
      setActiveTab("content");
    }
  }, [open, template]);

  const handleCopyVariable = (varName: string) => {
    navigator.clipboard.writeText(`{{${varName}}}`);
    setCopiedField(varName);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const addVariable = () => {
    setFormData((prev) => ({
      ...prev,
      variables: [
        ...prev.variables,
        { name: "", description: "", required: false, defaultValue: "", example: "" },
      ],
    }));
  };

  const removeVariable = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      variables: prev.variables.filter((_, i) => i !== index),
    }));
  };

  const updateVariable = (index: number, field: string, value: string | boolean) => {
    setFormData((prev) => ({
      ...prev,
      variables: prev.variables.map((v, i) =>
        i === index ? { ...v, [field]: value } : v
      ),
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] p-0 overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-4 border-b">
          <DialogTitle className="flex items-center gap-2">
            {template ? (
              <>
                <Edit className="h-5 w-5 text-primary" />
                Edit Template
              </>
            ) : (
              <>
                <Plus className="h-5 w-5 text-primary" />
                Create Email Template
              </>
            )}
          </DialogTitle>
          <DialogDescription>
            Design your email template with live preview. Use {"{{variableName}}"} syntax for dynamic content.
          </DialogDescription>
        </DialogHeader>

        <div className="flex h-[calc(90vh-180px)]">
          {/* Left Side - Editor */}
          <div className="flex-1 border-r overflow-hidden flex flex-col">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
              <TabsList className="mx-4 mt-4 grid w-auto grid-cols-4 bg-muted/50">
                <TabsTrigger value="content" className="gap-1.5">
                  <FileText className="h-4 w-4" />
                  Content
                </TabsTrigger>
                <TabsTrigger value="html" className="gap-1.5">
                  <Code className="h-4 w-4" />
                  HTML
                </TabsTrigger>
                <TabsTrigger value="variables" className="gap-1.5">
                  <Variable className="h-4 w-4" />
                  Variables
                </TabsTrigger>
                <TabsTrigger value="settings" className="gap-1.5">
                  <Palette className="h-4 w-4" />
                  Settings
                </TabsTrigger>
              </TabsList>

              <ScrollArea className="flex-1 p-4">
                <TabsContent value="content" className="mt-0 space-y-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="templateKey">Template Key *</Label>
                      <Input
                        id="templateKey"
                        value={formData.templateKey}
                        onChange={(e) => setFormData({ ...formData, templateKey: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, "_") })}
                        placeholder="welcome_email"
                        disabled={!!template}
                      />
                      <p className="text-xs text-muted-foreground">
                        Unique identifier (lowercase, underscores)
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="name">Template Name *</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Welcome Email"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Describe when this template is used..."
                      rows={2}
                    />
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="category">Category</Label>
                      <Select
                        value={formData.category}
                        onValueChange={(value: any) => setFormData({ ...formData, category: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="TRANSACTIONAL">Transactional</SelectItem>
                          <SelectItem value="MARKETING">Marketing</SelectItem>
                          <SelectItem value="NOTIFICATION">Notification</SelectItem>
                          <SelectItem value="SYSTEM">System</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="subject">Email Subject *</Label>
                      <Input
                        id="subject"
                        value={formData.subject}
                        onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                        placeholder="Welcome to {{companyName}}!"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Plain Text Version (Optional)</Label>
                    <Textarea
                      value={formData.textContent}
                      onChange={(e) => setFormData({ ...formData, textContent: e.target.value })}
                      placeholder="Plain text fallback for email clients that don't support HTML..."
                      rows={4}
                      className="font-mono text-sm"
                    />
                  </div>
                </TabsContent>

                <TabsContent value="html" className="mt-0 space-y-4">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>HTML Content *</Label>
                      <div className="flex items-center gap-2">
                        {formData.variables.length > 0 && (
                          <div className="flex items-center gap-1 flex-wrap">
                            <span className="text-xs text-muted-foreground">Insert:</span>
                            {formData.variables.slice(0, 5).map((v) => (
                              <Button
                                key={v.name}
                                variant="outline"
                                size="sm"
                                className="h-6 px-2 text-xs"
                                onClick={() => handleCopyVariable(v.name)}
                              >
                                {copiedField === v.name ? (
                                  <Check className="h-3 w-3 mr-1" />
                                ) : (
                                  <Copy className="h-3 w-3 mr-1" />
                                )}
                                {v.name}
                              </Button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <Textarea
                      value={formData.htmlContent}
                      onChange={(e) => setFormData({ ...formData, htmlContent: e.target.value })}
                      placeholder="<!DOCTYPE html>..."
                      rows={20}
                      className="font-mono text-sm"
                    />
                  </div>
                </TabsContent>

                <TabsContent value="variables" className="mt-0 space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Template Variables</Label>
                      <p className="text-xs text-muted-foreground mt-1">
                        Define dynamic placeholders using {"{{variableName}}"} syntax
                      </p>
                    </div>
                    <Button variant="outline" size="sm" onClick={addVariable}>
                      <Plus className="h-4 w-4 mr-1" />
                      Add Variable
                    </Button>
                  </div>

                  <div className="space-y-3">
                    {formData.variables.map((variable, index) => (
                      <Card key={index} className="p-4">
                        <div className="grid gap-3 sm:grid-cols-2">
                          <div className="space-y-1.5">
                            <Label className="text-xs">Variable Name</Label>
                            <Input
                              value={variable.name}
                              onChange={(e) => updateVariable(index, "name", e.target.value.replace(/[^a-zA-Z0-9_]/g, ""))}
                              placeholder="variableName"
                              className="font-mono"
                            />
                          </div>
                          <div className="space-y-1.5">
                            <Label className="text-xs">Description</Label>
                            <Input
                              value={variable.description}
                              onChange={(e) => updateVariable(index, "description", e.target.value)}
                              placeholder="What this variable represents"
                            />
                          </div>
                          <div className="space-y-1.5">
                            <Label className="text-xs">Example Value</Label>
                            <Input
                              value={variable.example || ""}
                              onChange={(e) => {
                                updateVariable(index, "example", e.target.value);
                                setPreviewVariables((prev) => ({
                                  ...prev,
                                  [variable.name]: e.target.value,
                                }));
                              }}
                              placeholder="Sample value for preview"
                            />
                          </div>
                          <div className="flex items-end gap-3">
                            <div className="flex items-center gap-2">
                              <Switch
                                checked={variable.required}
                                onCheckedChange={(checked) => updateVariable(index, "required", checked)}
                              />
                              <Label className="text-xs">Required</Label>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-destructive hover:text-destructive"
                              onClick={() => removeVariable(index)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                </TabsContent>

                <TabsContent value="settings" className="mt-0 space-y-4">
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <Label>Template Active</Label>
                      <p className="text-xs text-muted-foreground mt-1">
                        Inactive templates cannot be used for sending emails
                      </p>
                    </div>
                    <Switch
                      checked={formData.isActive}
                      onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
                    />
                  </div>

                  {/* Branding */}
                  <Card className="p-4 space-y-4">
                    <div>
                      <h4 className="font-medium">Brand Colors</h4>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Customize the color palette used in this template
                      </p>
                    </div>

                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label className="text-xs">Primary Color</Label>
                        <div className="flex items-center gap-2">
                          <input
                            type="color"
                            value={formData.styling.primaryColor || "#2BA88F"}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                styling: { ...formData.styling, primaryColor: e.target.value },
                              })
                            }
                            className="h-9 w-12 rounded border cursor-pointer bg-transparent"
                          />
                          <Input
                            value={formData.styling.primaryColor}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                styling: { ...formData.styling, primaryColor: e.target.value },
                              })
                            }
                            placeholder="#2BA88F"
                            className="font-mono text-sm flex-1"
                          />
                        </div>
                        <p className="text-xs text-muted-foreground">Buttons, links, and header gradient start</p>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-xs">Secondary Color</Label>
                        <div className="flex items-center gap-2">
                          <input
                            type="color"
                            value={formData.styling.secondaryColor || "#1D8A75"}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                styling: { ...formData.styling, secondaryColor: e.target.value },
                              })
                            }
                            className="h-9 w-12 rounded border cursor-pointer bg-transparent"
                          />
                          <Input
                            value={formData.styling.secondaryColor}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                styling: { ...formData.styling, secondaryColor: e.target.value },
                              })
                            }
                            placeholder="#1D8A75"
                            className="font-mono text-sm flex-1"
                          />
                        </div>
                        <p className="text-xs text-muted-foreground">Header gradient end and accents</p>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-xs">Header Background Override</Label>
                      <Input
                        value={formData.styling.headerBackground}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            styling: { ...formData.styling, headerBackground: e.target.value },
                          })
                        }
                        placeholder="e.g. #1a1a2e or linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                        className="text-sm"
                      />
                      <p className="text-xs text-muted-foreground">
                        Overrides the header background entirely. Supports solid colors or CSS gradients.
                      </p>
                    </div>
                  </Card>

                  <Card className="p-4 space-y-4">
                    <div>
                      <h4 className="font-medium">Logo & Typography</h4>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Set your logo and font for this template
                      </p>
                    </div>

                    <LogoUploadField
                      logoUrl={formData.styling.logoUrl}
                      onLogoChange={(url) =>
                        setFormData({
                          ...formData,
                          styling: { ...formData.styling, logoUrl: url },
                        })
                      }
                    />

                    <div className="space-y-2">
                      <Label className="text-xs">Font Family</Label>
                      <Select
                        value={formData.styling.fontFamily || "system"}
                        onValueChange={(value) =>
                          setFormData({
                            ...formData,
                            styling: { ...formData.styling, fontFamily: value === "system" ? "" : value },
                          })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="System Default" />
                        </SelectTrigger>
                        <SelectContent>
                          {fontFamilyOptions.map((opt) => (
                            <SelectItem key={opt.value || "system"} value={opt.value || "system"}>
                              <span style={{ fontFamily: opt.value || "inherit" }}>{opt.label}</span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        Email-safe font applied to the template body
                      </p>
                    </div>
                  </Card>

                  <Card className="p-4 space-y-4">
                    <div>
                      <h4 className="font-medium">Footer</h4>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Customize the footer text shown at the bottom of emails
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-xs">Footer Text</Label>
                      <Input
                        value={formData.styling.footerText}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            styling: { ...formData.styling, footerText: e.target.value },
                          })
                        }
                        placeholder="© 2026 Your Company. All rights reserved."
                        className="text-sm"
                      />
                    </div>
                  </Card>

                  <Card className="p-4">
                    <h4 className="font-medium mb-3">Template Information</h4>
                    <div className="grid gap-2 text-sm">
                      {template && (
                        <>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Created</span>
                            <span>{new Date(template.createdAt).toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Last Updated</span>
                            <span>{new Date(template.updatedAt).toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Template ID</span>
                            <span className="font-mono text-xs">{template.id}</span>
                          </div>
                        </>
                      )}
                    </div>
                  </Card>
                </TabsContent>
              </ScrollArea>
            </Tabs>
          </div>

          {/* Right Side - Preview */}
          <div className="w-[500px] bg-muted/30 p-4 overflow-auto">
            <LivePreview
              htmlContent={formData.htmlContent}
              variables={formData.variables}
              previewVariables={previewVariables}
              styling={formData.styling}
            />

            {/* Preview Variables */}
            <div className="mt-4 space-y-3">
              <Label className="text-sm font-medium">Preview Values</Label>
              <div className="space-y-2">
                {formData.variables.slice(0, 6).map((v) => (
                  <div key={v.name} className="flex items-center gap-2">
                    <Label className="text-xs w-24 truncate">{v.name}</Label>
                    <Input
                      value={previewVariables[v.name] || v.example || ""}
                      onChange={(e) =>
                        setPreviewVariables((prev) => ({
                          ...prev,
                          [v.name]: e.target.value,
                        }))
                      }
                      placeholder={v.example || `Enter ${v.name}`}
                      className="h-8 text-sm"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="px-6 py-4 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => onSave(formData)}
            disabled={!formData.templateKey || !formData.name || !formData.subject || !formData.htmlContent}
          >
            {template ? "Save Changes" : "Create Template"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Send Test Email Dialog
function SendTestDialog({
  open,
  onOpenChange,
  template,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  template: EmailTemplate | null;
}) {
  const [recipientEmail, setRecipientEmail] = useState("");
  const [variables, setVariables] = useState<Record<string, string>>({});
  const { toast } = useToast();

  useEffect(() => {
    if (open && template?.variables) {
      const vars: Record<string, string> = {};
      template.variables.forEach((v) => {
        vars[v.name] = v.example || "";
      });
      setVariables(vars);
    }
  }, [open, template]);

  const sendTestMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", `/api/admin/email-templates/${template?.id}/send-test`, {
        recipientEmail,
        variables,
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to send test email");
      }
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Test Email Sent",
        description: `Email sent to ${recipientEmail}`,
      });
      onOpenChange(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to Send",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="h-5 w-5 text-primary" />
            Send Test Email
          </DialogTitle>
          <DialogDescription>
            Send a test email using "{template?.name}" template
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="testEmail">Recipient Email *</Label>
            <Input
              id="testEmail"
              type="email"
              value={recipientEmail}
              onChange={(e) => setRecipientEmail(e.target.value)}
              placeholder="test@example.com"
            />
          </div>

          {template?.variables && template.variables.length > 0 && (
            <div className="space-y-3">
              <Label>Variable Values</Label>
              {template.variables.map((v) => (
                <div key={v.name} className="space-y-1">
                  <Label className="text-xs text-muted-foreground">
                    {v.name} {v.required && "*"}
                  </Label>
                  <Input
                    value={variables[v.name] || ""}
                    onChange={(e) =>
                      setVariables((prev) => ({ ...prev, [v.name]: e.target.value }))
                    }
                    placeholder={v.example || v.description}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => sendTestMutation.mutate()}
            disabled={!recipientEmail || sendTestMutation.isPending}
          >
            {sendTestMutation.isPending ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Send Test
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Send History Tab Component
function SendHistoryTab({ templates }: { templates: EmailTemplate[] | undefined }) {
  const [searchEmail, setSearchEmail] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [templateFilter, setTemplateFilter] = useState<string>("all");
  const [page, setPage] = useState(0);
  const pageSize = 20;

  // Build query params
  const queryParams = useMemo(() => {
    const params: Record<string, string> = {
      limit: String(pageSize),
      offset: String(page * pageSize),
    };
    if (templateFilter !== "all") params.templateId = templateFilter;
    return params;
  }, [page, templateFilter]);

  const { data: logsData, isLoading } = useQuery<{ logs: EmailLog[]; total: number }>({
    queryKey: ["email-logs", queryParams],
    queryFn: async () => {
      const searchParams = new URLSearchParams(queryParams);
      const response = await apiRequest("GET", `/api/admin/email-logs?${searchParams.toString()}`);
      if (!response.ok) throw new Error("Failed to fetch email logs");
      return response.json();
    },
  });

  // Client-side filter for search and status (API only supports templateId filter)
  const filteredLogs = useMemo(() => {
    if (!logsData?.logs) return [];
    return logsData.logs.filter((log) => {
      const matchesSearch =
        !searchEmail ||
        log.recipientEmail.toLowerCase().includes(searchEmail.toLowerCase()) ||
        (log.recipientName && log.recipientName.toLowerCase().includes(searchEmail.toLowerCase()));
      const matchesStatus = statusFilter === "all" || log.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [logsData, searchEmail, statusFilter]);

  // Stats from all fetched logs
  const stats = useMemo(() => {
    const logs = logsData?.logs || [];
    return {
      total: logsData?.total || 0,
      delivered: logs.filter((l) => l.status === "delivered").length,
      failed: logs.filter((l) => l.status === "failed").length,
      bounced: logs.filter((l) => l.status === "bounced").length,
    };
  }, [logsData]);

  const totalPages = Math.ceil((logsData?.total || 0) / pageSize);

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
  }, [templateFilter]);

  // Template key -> name lookup
  const templateNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    templates?.forEach((t) => {
      map[t.id] = t.name;
      map[t.templateKey] = t.name;
    });
    return map;
  }, [templates]);

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        {[
          { label: "Total Sent", value: stats.total, icon: Send, color: "text-blue-500", bg: "bg-blue-500/10" },
          { label: "Delivered", value: stats.delivered, icon: CheckCircle2, color: "text-green-500", bg: "bg-green-500/10" },
          { label: "Failed", value: stats.failed, icon: XCircle, color: "text-red-500", bg: "bg-red-500/10" },
          { label: "Bounced", value: stats.bounced, icon: AlertCircle, color: "text-amber-500", bg: "bg-amber-500/10" },
        ].map((stat) => (
          <Card key={stat.label}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={cn("p-2 rounded-lg", stat.bg)}>
                  <stat.icon className={cn("h-4 w-4", stat.color)} />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{stat.label}</p>
                  <p className="text-xl font-bold">{stat.value}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by recipient email..."
            value={searchEmail}
            onChange={(e) => setSearchEmail(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-[160px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="queued">Queued</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="delivered">Delivered</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
            <SelectItem value="bounced">Bounced</SelectItem>
          </SelectContent>
        </Select>
        <Select value={templateFilter} onValueChange={setTemplateFilter}>
          <SelectTrigger className="w-full sm:w-[200px]">
            <SelectValue placeholder="Template" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Templates</SelectItem>
            {templates?.map((t) => (
              <SelectItem key={t.id} value={t.id}>
                {t.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      {isLoading ? (
        <Card>
          <CardContent className="p-6 space-y-3">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </CardContent>
        </Card>
      ) : filteredLogs.length === 0 ? (
        <Card className="p-12">
          <div className="text-center">
            <Inbox className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-1">No emails sent yet</h3>
            <p className="text-sm text-muted-foreground">
              Send a test email from any template to see delivery history here.
            </p>
          </div>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Recipient</TableHead>
                <TableHead>Template</TableHead>
                <TableHead>Subject</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Sent At</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredLogs.map((log) => {
                const status = statusConfig[log.status] || statusConfig.queued;
                const StatusIcon = status.icon;
                return (
                  <TableRow key={log.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium text-sm">{log.recipientEmail}</p>
                        {log.recipientName && (
                          <p className="text-xs text-muted-foreground">{log.recipientName}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">
                        {(log.templateId && templateNameMap[log.templateId]) ||
                          (log.templateKey && templateNameMap[log.templateKey]) ||
                          log.templateKey ||
                          "—"}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm line-clamp-1 max-w-[250px]">{log.subject}</span>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="secondary"
                        className={cn("gap-1 font-medium", status.className)}
                      >
                        <StatusIcon className="h-3 w-3" />
                        {status.label}
                      </Badge>
                      {log.errorMessage && (
                        <p className="text-xs text-red-500 mt-1 line-clamp-1 max-w-[200px]">
                          {log.errorMessage}
                        </p>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground">
                        {log.sentAt
                          ? new Date(log.sentAt).toLocaleString()
                          : new Date(log.createdAt).toLocaleString()}
                      </span>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t">
              <p className="text-sm text-muted-foreground">
                Showing {page * pageSize + 1}–{Math.min((page + 1) * pageSize, logsData?.total || 0)} of {logsData?.total || 0}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 0}
                  onClick={() => setPage((p) => p - 1)}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages - 1}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

// Main Page Component
export default function EmailTemplatesPage() {
  const [activePageTab, setActivePageTab] = useState("templates");
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [editorOpen, setEditorOpen] = useState(false);
  const [sendTestOpen, setSendTestOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<EmailTemplate | null>(null);
  const { toast } = useToast();

  // Fetch templates
  const { data: templates, isLoading } = useQuery<EmailTemplate[]>({
    queryKey: ["email-templates"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/admin/email-templates");
      if (!response.ok) throw new Error("Failed to fetch templates");
      return response.json();
    },
  });

  // Create/Update mutation
  const saveMutation = useMutation({
    mutationFn: async (data: TemplateFormData) => {
      const url = selectedTemplate
        ? `/api/admin/email-templates/${selectedTemplate.id}`
        : "/api/admin/email-templates";
      const method = selectedTemplate ? "PUT" : "POST";
      const response = await apiRequest(method, url, data);
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to save template");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["email-templates"] });
      toast({
        title: selectedTemplate ? "Template Updated" : "Template Created",
        description: "Your email template has been saved successfully.",
      });
      setEditorOpen(false);
      setSelectedTemplate(null);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await apiRequest("DELETE", `/api/admin/email-templates/${id}`);
      if (!response.ok) throw new Error("Failed to delete template");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["email-templates"] });
      toast({
        title: "Template Deleted",
        description: "The email template has been removed.",
      });
      setDeleteDialogOpen(false);
      setTemplateToDelete(null);
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to delete template",
        variant: "destructive",
      });
    },
  });

  // Filter templates
  const filteredTemplates = useMemo(() => {
    if (!templates) return [];
    return templates.filter((t) => {
      const matchesSearch =
        !search ||
        t.name.toLowerCase().includes(search.toLowerCase()) ||
        t.templateKey.toLowerCase().includes(search.toLowerCase());
      const matchesCategory = categoryFilter === "all" || t.category === categoryFilter;
      return matchesSearch && matchesCategory;
    });
  }, [templates, search, categoryFilter]);

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-display font-bold tracking-tight">Email Templates</h1>
            <Badge variant="premium" className="gap-1">
              <Shield className="h-3 w-3" />
              Admin Only
            </Badge>
          </div>
          <p className="text-muted-foreground">
            Design and manage email templates with live preview and variable support.
          </p>
        </div>
        <Button onClick={() => { setSelectedTemplate(null); setEditorOpen(true); }} className="gap-2">
          <Plus className="h-4 w-4" />
          New Template
        </Button>
      </div>

      {/* Page-level Tabs */}
      <Tabs value={activePageTab} onValueChange={setActivePageTab}>
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="templates" className="gap-2">
            <Mail className="h-4 w-4" />
            Templates
          </TabsTrigger>
          <TabsTrigger value="history" className="gap-2">
            <History className="h-4 w-4" />
            Send History
          </TabsTrigger>
        </TabsList>

        <TabsContent value="templates" className="mt-6 space-y-6">

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        {[
          { label: "Total Templates", value: templates?.length || 0, icon: Mail, color: "text-blue-500", bg: "bg-blue-500/10" },
          { label: "Active", value: templates?.filter(t => t.isActive).length || 0, icon: Check, color: "text-green-500", bg: "bg-green-500/10" },
          { label: "Transactional", value: templates?.filter(t => t.category === "TRANSACTIONAL").length || 0, icon: FileText, color: "text-amber-500", bg: "bg-amber-500/10" },
          { label: "Marketing", value: templates?.filter(t => t.category === "MARKETING").length || 0, icon: Sparkles, color: "text-purple-500", bg: "bg-purple-500/10" },
        ].map((stat) => (
          <Card key={stat.label}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={cn("p-2 rounded-lg", stat.bg)}>
                  <stat.icon className={cn("h-4 w-4", stat.color)} />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{stat.label}</p>
                  <p className="text-xl font-bold">{stat.value}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search templates..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-full sm:w-[180px]">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            <SelectItem value="TRANSACTIONAL">Transactional</SelectItem>
            <SelectItem value="MARKETING">Marketing</SelectItem>
            <SelectItem value="NOTIFICATION">Notification</SelectItem>
            <SelectItem value="SYSTEM">System</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Templates Grid */}
      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[...Array(6)].map((_, i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-5 w-40" />
                <Skeleton className="h-4 w-24" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-20 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : filteredTemplates.length === 0 ? (
        <Card className="p-12">
          <div className="text-center">
            <Mail className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-1">
              {templates?.length === 0 ? "No templates yet" : "No templates found"}
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              {templates?.length === 0
                ? "Create your first email template to get started"
                : "Try adjusting your search or filters"}
            </p>
            {templates?.length === 0 && (
              <Button onClick={() => { setSelectedTemplate(null); setEditorOpen(true); }}>
                <Plus className="h-4 w-4 mr-2" />
                Create Template
              </Button>
            )}
          </div>
        </Card>
      ) : (
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {filteredTemplates.map((template) => {
            const colors = categoryColors[template.category];
            const categoryLabels: Record<string, string> = {
              TRANSACTIONAL: "Account & Security",
              MARKETING: "Promotions",
              NOTIFICATION: "Alerts",
              SYSTEM: "System",
            };
            const categoryIcons: Record<string, typeof Mail> = {
              TRANSACTIONAL: Shield,
              MARKETING: Sparkles,
              NOTIFICATION: AlertCircle,
              SYSTEM: FileText,
            };
            const CategoryIcon = categoryIcons[template.category] || Mail;

            return (
              <Card
                key={template.id}
                className={cn(
                  "group relative overflow-hidden transition-all duration-200",
                  "hover:shadow-lg hover:border-primary/50",
                  !template.isActive && "opacity-50 grayscale"
                )}
              >
                {/* Status indicator */}
                {!template.isActive && (
                  <div className="absolute top-3 right-3 z-10">
                    <Badge variant="secondary" className="bg-slate-200 text-slate-600 text-xs font-medium">
                      Disabled
                    </Badge>
                  </div>
                )}

                <div className="p-5 space-y-4">
                  {/* Icon & Category Header */}
                  <div className="flex items-start gap-4">
                    <div className={cn(
                      "p-3 rounded-xl shrink-0",
                      template.category === "TRANSACTIONAL" && "bg-blue-100 dark:bg-blue-900/30",
                      template.category === "MARKETING" && "bg-purple-100 dark:bg-purple-900/30",
                      template.category === "NOTIFICATION" && "bg-amber-100 dark:bg-amber-900/30",
                      template.category === "SYSTEM" && "bg-slate-100 dark:bg-slate-800"
                    )}>
                      <CategoryIcon className={cn(
                        "h-6 w-6",
                        template.category === "TRANSACTIONAL" && "text-blue-600 dark:text-blue-400",
                        template.category === "MARKETING" && "text-purple-600 dark:text-purple-400",
                        template.category === "NOTIFICATION" && "text-amber-600 dark:text-amber-400",
                        template.category === "SYSTEM" && "text-slate-600 dark:text-slate-400"
                      )} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className={cn("text-xs font-medium uppercase tracking-wide", colors.text)}>
                        {categoryLabels[template.category]}
                      </span>
                      <h3 className="font-semibold text-lg leading-tight mt-0.5 truncate">
                        {template.name}
                      </h3>
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-sm text-muted-foreground leading-relaxed line-clamp-2 min-h-[2.5rem]">
                    {template.description || "No description provided"}
                  </p>

                  {/* Subject Preview Card */}
                  <div className="bg-gradient-to-br from-muted/50 to-muted/30 rounded-lg p-4 border border-border/50">
                    <div className="flex items-center gap-2 mb-2">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground font-medium">Subject Line</span>
                    </div>
                    <p className="font-medium text-sm leading-snug line-clamp-2">
                      {template.subject}
                    </p>
                  </div>

                  {/* Quick Stats - Simplified */}
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <div className="flex items-center gap-1.5">
                      <Variable className="h-3.5 w-3.5" />
                      <span>{template.variables?.length || 0} fields</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <RefreshCw className="h-3.5 w-3.5" />
                      <span>Updated {new Date(template.updatedAt).toLocaleDateString()}</span>
                    </div>
                  </div>

                  {/* Actions - Clear and prominent */}
                  <div className="flex items-center gap-2 pt-3 border-t">
                    <Button
                      variant="default"
                      size="default"
                      className="flex-1 gap-2"
                      onClick={() => { setSelectedTemplate(template); setEditorOpen(true); }}
                    >
                      <Edit className="h-4 w-4" />
                      Edit Template
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      title="Send test email"
                      onClick={() => { setSelectedTemplate(template); setSendTestOpen(true); }}
                    >
                      <Send className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      className="hover:bg-destructive/10 hover:text-destructive hover:border-destructive/30"
                      title="Delete template"
                      onClick={() => { setTemplateToDelete(template); setDeleteDialogOpen(true); }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

        </TabsContent>

        <TabsContent value="history" className="mt-6">
          <SendHistoryTab templates={templates} />
        </TabsContent>
      </Tabs>

      {/* Editor Dialog */}
      <TemplateEditorDialog
        open={editorOpen}
        onOpenChange={setEditorOpen}
        template={selectedTemplate}
        onSave={(data) => saveMutation.mutate(data)}
      />

      {/* Send Test Dialog */}
      <SendTestDialog
        open={sendTestOpen}
        onOpenChange={setSendTestOpen}
        template={selectedTemplate}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Template?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{templateToDelete?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => templateToDelete && deleteMutation.mutate(templateToDelete.id)}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
